import {
    REPLAY_OUTBOX_MAX_UPLOAD_BYTES, SessionReplayOutbox, type ReplayOutboxScope,
} from './sessionReplayOutbox';
import {ReplayStateStore, type ReplayCreationDescriptor} from './sessionReplayState';
import {
    ReplayTransportError, SessionReplayTransport, type ReplaySessionResponse,
    type ReplayTransportOptions,
} from '../rest/sessionReplayTransport';

export interface SessionReplayRuntimeDependencies {
    descriptor: ReplayCreationDescriptor;
    transport: Pick<SessionReplayTransport, 'capabilities' | 'create' | 'upload' | 'complete' | 'heartbeat' | 'status'>;
    outbox?: SessionReplayOutbox;
    state?: ReplayStateStore;
    onInterrupted?: (error: Error) => void;
    /** Cancels network work from the authentication generation that created
     * this runtime without preventing its memory queue from reaching IndexedDB. */
    lifetimeSignal?: AbortSignal;
}

export interface ReplayRuntimeStatus {
    session: ReplaySessionResponse;
    pendingEvents: number;
    nextChunkIndex: number;
}

const scopeOf = (descriptor: ReplayCreationDescriptor): ReplayOutboxScope => ({
    firmId: descriptor.firmId, userId: descriptor.userId, sessionId: descriptor.sessionId,
});

function failure(error: unknown, fallback: string): Error {
    return error instanceof Error ? error : new Error(fallback);
}

/**
 * Coordinates one fixed recording identity. IndexedDB owns accepted events,
 * the transport owns authenticated requests, and this class advances either
 * side only after the other has supplied an exact acknowledgement.
 */
export class SessionReplayRuntime {
    readonly descriptor: Readonly<ReplayCreationDescriptor>;
    readonly scope: Readonly<ReplayOutboxScope>;
    private readonly transport: SessionReplayRuntimeDependencies['transport'];
    private readonly outbox: SessionReplayOutbox;
    private readonly state: ReplayStateStore;
    private readonly onInterrupted?: (error: Error) => void;
    private readonly lifetimeSignal?: AbortSignal;
    private readonly controller = new AbortController();
    private activation?: Promise<ReplaySessionResponse>;
    private draining?: Promise<void>;
    private terminal = false;
    private acceptedBytes = 0;
    private acceptedChunks = 0;
    private retryAt = 0;
    private retryCount = 0;
    private deferred?: ReplayTransportError;

    constructor(deps: SessionReplayRuntimeDependencies) {
        this.descriptor = Object.freeze({...deps.descriptor});
        this.scope = Object.freeze(scopeOf(deps.descriptor));
        this.transport = deps.transport;
        this.outbox = deps.outbox ?? new SessionReplayOutbox(this.scope);
        this.state = deps.state ?? new ReplayStateStore();
        this.onInterrupted = deps.onInterrupted;
        this.lifetimeSignal = deps.lifetimeSignal;
    }

    activate(options: ReplayTransportOptions = {}): Promise<ReplaySessionResponse> {
        if (this.terminal) return Promise.reject(new Error('Replay runtime is closed.'));
        if (Date.now() < this.retryAt) return Promise.reject(this.deferred);
        if (!this.activation) {
            const pending = this.activateOnce(options).catch(async error => {
                if (this.activation === pending) this.activation = undefined;
                await this.interrupt(error);
                throw error;
            });
            this.activation = pending;
        }
        return this.activation;
    }

    async append(events: readonly unknown[]): Promise<void> {
        this.assertOpen();
        await this.outbox.append(events);
    }

    drain(options: ReplayTransportOptions = {}): Promise<void> {
        this.assertOpen();
        if (!this.draining) {
            this.draining = this.drainOnce(options).catch(async error => {
                await this.interrupt(error);
                throw error;
            }).finally(() => { this.draining = undefined; });
        }
        return this.draining;
    }

    async heartbeat(options: ReplayTransportOptions = {}): Promise<ReplaySessionResponse> {
        await this.activate(options);
        this.assertOpen();
        try {
            return await this.transport.heartbeat(this.scope, this.withSignal(options));
        } catch (error) {
            if (error instanceof ReplayTransportError && error.retryable) {
                await this.interrupt(error);
                throw error;
            }
            if (!this.canReopen(error)) throw error;
            this.activation = undefined;
            await this.activate(options);
            return this.transport.heartbeat(this.scope, this.withSignal(options));
        }
    }

    async status(options: ReplayTransportOptions = {}): Promise<ReplayRuntimeStatus> {
        await this.activate(options);
        this.assertOpen();
        const [session, pending] = await Promise.all([
            this.transport.status(this.scope, this.withSignal(options)), this.outbox.status(),
        ]);
        return {...pending, session};
    }

    localStatus(): Promise<{pendingEvents: number; nextChunkIndex: number}> {
        this.assertOpen();
        return this.outbox.status();
    }

    localCompletionState(): Promise<{pendingEvents: number; nextChunkIndex: number; totalEvents: number}> {
        this.assertOpen();
        return this.outbox.completionState();
    }

    async chunkSlotUsage(): Promise<{acknowledged: number; pending: number; total: number; bytes: number}> {
        this.assertOpen();
        const usage = await this.outbox.chunkSlotUsage();
        return {...usage, bytes: this.acceptedBytes + usage.bytesSerialized};
    }

    /** Dispose only this exact durable scope after authoritative remote proof,
     * or an unconfirmed creation with no accepted events. */
    async discardLocal(): Promise<void> {
        this.assertOpen();
        await this.outbox.discard();
        await this.state.discard(this.scope);
        this.terminal = true;
    }

    async beginCompletion(summary?: unknown): Promise<void> {
        this.assertOpen();
        await this.state.prepare(this.descriptor);
        await this.state.mark(this.scope, 'closing', summary);
    }

    async complete(summary?: unknown, options: ReplayTransportOptions = {}): Promise<ReplaySessionResponse> {
        const retained = await this.state.get(this.scope);
        if (retained?.completion) {
            await this.finishLocalCompletion(retained.completion);
            return retained.completion;
        }
        await this.beginCompletion(summary);
        try {
            await this.activate(options);
            await this.drain(options);
            const totals = await this.outbox.completionState();
            if (totals.pendingEvents !== 0) throw new Error('Replay still has pending local events.');
            const session = await this.transport.complete(this.scope, {
                summary, expectedChunkCount: totals.nextChunkIndex, expectedTotalEvents: totals.totalEvents,
            }, this.withSignal(options));
            await this.state.confirm(this.scope, session);
            await this.state.recordCompletion(this.scope, session);
            await this.finishLocalCompletion(session);
            return session;
        } catch (error) {
            // Completion is an owner decision, not a transient recording phase.
            // Preserve it across failed drains/requests so a reload finishes the
            // exact session instead of resuming capture into a stream the user
            // already asked us to close.
            try { await this.state.mark(this.scope, 'closing'); }
            catch { /* Preserve the completion failure and retained outbox. */ }
            throw error;
        }
    }

    async interrupt(error: unknown, notify = true): Promise<void> {
        const problem = failure(error, 'Replay recording was interrupted.');
        if (error instanceof ReplayTransportError && error.retryable) {
            if (error === this.deferred && Date.now() < this.retryAt) return;
            // A failed upload remains prepared in IndexedDB. Keep the existing
            // recorder and handshake; retries send those same immutable bytes.
            this.retryAt = Date.now() + Math.max(error.retryAfterMs ?? 0,
                Math.min(60_000, 2_000 * 2 ** Math.min(this.retryCount++, 5)));
            this.deferred = error;
            await this.state.mark(this.scope, 'interrupted', undefined, this.retryAt);
            if (notify) this.onInterrupted?.(problem);
            return;
        }
        try { await this.state.mark(this.scope, 'interrupted'); }
        catch { /* Preserve the original failure; IndexedDB retains any accepted rows. */ }
        if (notify) this.onInterrupted?.(problem);
    }

    close(): void {
        if (!this.controller.signal.aborted) this.controller.abort(new DOMException('Replay runtime closed.', 'AbortError'));
        this.terminal = true;
        this.outbox.close();
        this.state.close();
    }

    private async activateOnce(options: ReplayTransportOptions): Promise<ReplaySessionResponse> {
        const retained = await this.state.prepare(this.descriptor);
        if (retained.retryAt && retained.retryAt > Date.now()) {
            this.retryAt = retained.retryAt;
            this.deferred = new ReplayTransportError('http', 'Replay upload remains deferred.', 503, undefined, retained.retryAt - Date.now());
            throw this.deferred;
        }
        const request = this.withSignal(options);
        const capabilities = await this.transport.capabilities(this.scope, request);
        if (capabilities.maxUploadBytes < REPLAY_OUTBOX_MAX_UPLOAD_BYTES) {
            throw new ReplayTransportError('upgrade-required', 'Replay server upload limits do not support this recorder.', 426);
        }
        // A confirmed local state must never recreate a deleted/expired server
        // row. The existing create route performs an existing-only handshake.
        const session = await this.transport.create(this.descriptor, request, retained.confirmed);
        await this.state.confirm(this.scope, session);
        this.acceptedBytes = Number(session.totalBytesCompressed ?? 0);
        this.acceptedChunks = session.chunkCount;
        if (!Number.isSafeInteger(this.acceptedBytes) || this.acceptedBytes < 0) throw new ReplayTransportError('invalid-response', 'Invalid replay byte total.');
        if (session.status !== 'recording' && retained.phase !== 'closing') {
            throw new ReplayTransportError('session-closed', 'Replay session is not recording.', 409);
        }
        return session;
    }

    private async drainOnce(options: ReplayTransportOptions): Promise<void> {
        await this.activate(options);
        for (;;) {
            this.assertOpen();
            const chunk = await this.outbox.prepare();
            if (!chunk) return;
            let acknowledgement;
            try {
                acknowledgement = await this.transport.upload(this.scope, chunk, this.withSignal(options));
            } catch (error) {
                if (!this.canReopen(error)) throw error;
                // The server can lazily close a stale recording after this
                // runtime has already activated. Retry the idempotent create
                // handshake once so the exact descriptor/session can reopen.
                this.activation = undefined;
                await this.activate(options);
                acknowledgement = await this.transport.upload(this.scope, chunk, this.withSignal(options));
            }
            if (!await this.outbox.acknowledge(acknowledgement)) {
                throw new Error('Replay server acknowledgement did not match the retained chunk.');
            }
            if (acknowledgement.chunkIndex >= this.acceptedChunks) {
                this.acceptedBytes += acknowledgement.bytesCompressed;
                this.acceptedChunks = acknowledgement.chunkIndex + 1;
            }
            this.retryCount = 0;
            this.retryAt = 0;
            this.deferred = undefined;
        }
    }

    private canReopen(error: unknown): boolean {
        return error instanceof ReplayTransportError &&
            !['resume_session_missing', 'session_deleted'].includes(error.serverCode ?? '') &&
            ['session-closed', 'not-found', 'gone', 'conflict'].includes(error.code);
    }

    private async finishLocalCompletion(session: ReplaySessionResponse): Promise<void> {
        await this.outbox.destroyCompleted({chunkCount: session.chunkCount, totalEvents: session.totalEvents});
        await this.state.complete(this.scope);
        this.terminal = true;
    }

    private withSignal(options: ReplayTransportOptions): ReplayTransportOptions {
        const signals = [this.controller.signal, ...(this.lifetimeSignal ? [this.lifetimeSignal] : []),
            ...(options.signal ? [options.signal] : [])];
        const signal = signals.length === 1 ? signals[0] : AbortSignal.any(signals);
        return {...options, signal};
    }

    private assertOpen(): void {
        if (this.terminal || this.controller.signal.aborted) throw new Error('Replay runtime is closed.');
    }
}
